import React from "react";
import { Modal, Row, Col, Card, ListGroup, Spinner, Badge } from "react-bootstrap";
import { 
  FaTimes, FaBuilding, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, 
  FaIdCard, FaMoneyBillWave, FaCalendar, FaFileAlt, 
} from "react-icons/fa";
import "../style/ViewMoreModal.css";
import { getTotalCashSecurity } from "../../../service/api/SecurityService";
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import AccountDocuments from "./AccountDocuments"; // Import du composant documents
import ImageWithApi from "../../../accountComponent/ImageWithSas";
import { formatDate, formatNumber } from "../../../service/utils/formatters";

export default function ViewMoreModal({ show, onHide, account }) {
  const [totalCashSecurity, setTotalCashSecurity] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const selectedDomain = useSelectedDomain();

  React.useEffect(() => {
    const fetchCash = async () => {
      if (!selectedDomain?.id || !account) return;
      setLoading(true);
      try {
        const cash = await getTotalCashSecurity(account.id, selectedDomain.id);
        setTotalCashSecurity(cash);
      } catch {
        setTotalCashSecurity(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (show && account) {
      fetchCash();
    }
  }, [account, selectedDomain, show]);

  if (!account) {
    return null;
  }

  const isCompany = ['Entreprise', 'Banques', 'Assurance', 'Autres Entreprises', 'Association','autres','Opc','Opcvm','Fia','Assurance','Issuer']
    .includes(account.typeName);

  const getFieldIcon = (fieldName) => {
    const icons = {
      nom: isCompany ? FaBuilding : FaUser,
      email: FaEnvelope,
      telephone: FaPhone,
      adresse: FaMapMarkerAlt,
      code: FaIdCard,
      cash: FaMoneyBillWave,
      date: FaCalendar,
      document: FaFileAlt
    };
    
    return icons[fieldName] || FaFileAlt;
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      className="view-more-modal"
      backdrop="static"
      scrollable
    >
      <Modal.Header className="modal-header-custom ">
        <Modal.Title className="modal-title-custom">
          <div className="d-flex align-items-center">
            <ImageWithApi
              accountId={account.id}
              accountType={account.typeName}
              email={account.email}
              alt={`${account.companyName || `${account.firstName} ${account.lastName}`}`}
              className="modal-avatar me-3"
              style={{ 
                width: '60px', 
                height: '60px', 
                flexShrink: 0,
                borderRadius: '50%',
                objectFit: 'cover'
              }}
              imageType="auto"
              fallbackToGravatar={true}
            />
            <div>
              <h4 className="mb-1">{account.companyName || `${account.firstName} ${account.lastName}`}</h4>
              <Badge bg="primary" className="account-type-badge">
                {account.accountTypeName === "Particulier" ? "Particulier" : account.accountTypeName}
              </Badge>
            </div>
          </div>
        </Modal.Title>
        <button 
          type="button" 
          className="btn-close-custom" 
          onClick={onHide}
          aria-label="Fermer"
        >
          <FaTimes />
        </button>
      </Modal.Header>

      <Modal.Body className="modal-body-custom p-0">
        <Row className="g-0">
          {/* Section Image */}
          <Col xs={12} lg={5} className="image-section">
            <div className="image-container" style={{ 
              height: '350px', 
              width: '100%', 
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <ImageWithApi
                accountId={account.id}
                accountType={account.typeName}
                email={account.email}
                alt={`${account.companyName || `${account.firstName} ${account.lastName}`}`}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
                imageType="auto"
                fallbackToGravatar={true}
              />
            </div>
            {/* Nom */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    {React.createElement(getFieldIcon('nom'))}
                  </div>
                  <div className="info-content">
                    <span className="info-label">
                      {isCompany ? "Nom de l'entreprise" : "Nom complet"}
                    </span>
                    <span className="info-value">
                      {account.companyName || `${account.firstName} ${account.lastName}`}
                    </span>
                  </div>
                </ListGroup.Item>

                {/* Email */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaEnvelope />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Email</span>
                    <span className="info-value">{account.email || "Non renseigné"}</span>
                  </div>
                </ListGroup.Item>
            
            {/* Cartes de résumé */}
            <div className="summary-cards">
              <Card className="summary-card cash-card">
                <Card.Body>
                  <FaMoneyBillWave className="card-icon" />
                  <div className="card-content">
                    <span className="card-label">Cash Disponible</span>
                    <span className="card-value account-count">
                      {formatNumber(account.cash || 0)} FCFA
                    </span>
                  </div>
                </Card.Body>
              </Card>

              <Card className="summary-card security-card">
                <Card.Body>
                  <FaMoneyBillWave className="card-icon" />
                  <div className="card-content">
                    <span className="card-label">Valeur Titres</span>
                    <span className="card-value account-count" >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        `${formatNumber(totalCashSecurity || 0)} FCFA`
                      )}
                    </span>
                  </div>
                </Card.Body>
              </Card>

              <Card className="summary-card security-card">
                <Card.Body>
                  <FaMoneyBillWave className="card-icon" />
                  <div className="card-content">
                    <span className="card-label">Capital</span>
                    <span className="card-value account-count" >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        `${formatNumber(account.capital || 0)} FCFA`
                      )}
                    </span>
                  </div>
                </Card.Body>
              </Card>

              <Card className="summary-card security-card">
                <Card.Body>
                  <FaMoneyBillWave className="card-icon" />
                  <div className="card-content">
                    <span className="card-label">Revenu</span>
                    <span className="card-value account-count" >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        `${formatNumber(account.yearlyRevenue || 0)} FCFA`
                      )}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Section Informations */}
          <Col xs={12} lg={7} className="info-section">
            <div className="info-content">
              <h5 className="section-title">Informations du compte</h5>
              
              <ListGroup variant="flush" className="info-list">
                {/* Nom */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    {React.createElement(getFieldIcon('nom'))}
                  </div>
                  <div className="info-content">
                    <span className="info-label">
                      {isCompany ? "Nom de l'entreprise" : "Nom complet"}
                    </span>
                    <span className="info-value">
                      {account.companyName || `${account.firstName} ${account.lastName}`}
                    </span>
                  </div>
                </ListGroup.Item>

                {/* Email */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaEnvelope />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Email</span>
                    <span className="info-value">{account.email || "Non renseigné"}</span>
                  </div>
                </ListGroup.Item>

                {/* Téléphone */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaPhone />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Téléphone</span>
                    <span className="info-value">{account.phoneNumber || "Non renseigné"}</span>
                  </div>
                </ListGroup.Item>

                {/* Adresse */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="info-content">
                    <span className="info-label">
                      {isCompany ? "Adresse de l'entreprise" : "Adresse"}
                    </span>
                    <span className="info-value">
                      {isCompany ? account.company_adress : account.address || "Non renseigné"}
                    </span>
                  </div>
                </ListGroup.Item>

                {/* Champs spécifiques selon le type */}
                {isCompany ? (
                  <>
                    {/* Forme légale */}
                    <ListGroup.Item className="info-item">
                      <div className="info-icon">
                        <FaBuilding />
                      </div>
                      <div className="info-content">
                        <span className="info-label">Forme légale</span>
                        <span className="info-value">{account.legalForm || "Non renseigné"}</span>
                      </div>
                    </ListGroup.Item>

                    {/* ISIN */}
                    <ListGroup.Item className="info-item">
                      <div className="info-icon">
                        <FaIdCard />
                      </div>
                      <div className="info-content">
                        <span className="info-label">ISIN</span>
                        <span className="info-value">{account.isin || "Non renseigné"}</span>
                      </div>
                    </ListGroup.Item>
                  </>
                ) : (
                  <>
                  {/* /* Date de naissance pour les particuliers */ }
                    <ListGroup.Item className="info-item">
                      <div className="info-icon">
                        <FaCalendar />
                      </div>
                      <div className="info-content">
                        <span className="info-label">Date de naissance</span>
                        <span className="info-value">{formatDate(account.dob)}</span>
                      </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="info-item">
                      <div className="info-icon">
                        <FaCalendar />
                      </div>
                      <div className="info-content">
                        <span className="info-label">Ville de naissance</span>
                        <span className="info-value">{(account.cityOfBirth)}</span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item className="info-item">
                      <div className="info-icon">
                        <FaCalendar />
                      </div>
                      <div className="info-content">
                        <span className="info-label">Pays de naissance</span>
                        <span className="info-value">{(account.countryOfBirth)}</span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item className="info-item">
                      <div className="info-icon">
                        <FaCalendar />
                      </div>
                      <div className="info-content">
                        <span className="info-label">Numero Identification Fiscale (NIU)</span>
                        <span className="info-value">{(account.NumeroIdentificationFiscale)}</span>
                      </div>
                    </ListGroup.Item>
                  </>
                )}

                {/* Code */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaIdCard />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Code client</span>
                    <span className="info-value code-value">{account.plainCode || "Non attribué"}</span>
                  </div>
                </ListGroup.Item>

                {/* Profession */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaUser />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Profession</span>
                    <span className="info-value">{account.job || "Non renseigné"}</span>
                  </div>
                </ListGroup.Item>

                {/* Description */}
                {account.description && (
                  <ListGroup.Item className="info-item">
                    <div className="info-icon">
                      <FaFileAlt />
                    </div>
                    <div className="info-content">
                      <span className="info-label">Description</span>
                      <span className="info-value">{account.description}</span>
                    </div>
                  </ListGroup.Item>
                )}

                {/* Dates */}
                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaCalendar />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Date de création</span>
                    <span className="info-value">{formatDate(account.creation_date)}</span>
                  </div>
                </ListGroup.Item>

                <ListGroup.Item className="info-item">
                  <div className="info-icon">
                    <FaCalendar />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Dernière modification</span>
                    <span className="info-value">{formatDate(account.last_update_date)}</span>
                  </div>
                </ListGroup.Item>
              </ListGroup>
            </div>
          </Col>
        </Row>

        {/* SECTION DOCUMENTS - AJOUTÉE ICI */}
        {account && account.id && (
          <div className="documents-section mt-4">
            <AccountDocuments accountId={account.id} />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}